/**
 * settings/cards/DevToolsCard.tsx
 * --------------------------------
 * What: Developer utilities exposed in development builds (e.g., toggles, logs).
 * Why: Speeds up debugging and QA without cluttering production UI.
 */
import React, { useMemo } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../../../lib/ThemeContext';
import env from '../../../../src/config/env';

interface Props {
  expanded: boolean;
  onToggle: () => void;
}

export default function DevToolsCard({ expanded, onToggle }: Props) {
  const { theme, themeType } = useTheme();

  // Glow shadow for blue neon
  const glowStyle = useMemo(() => (
    themeType === 'blueNeon'
      ? (Platform.OS === 'web'
          ? { boxShadow: `0 0 6px 1px ${theme.accent}` } as any
          : {
              shadowColor: theme.accent,
              shadowOffset: { width: 0, height: 0 },
              shadowOpacity: 0.5,
              shadowRadius: 6,
              elevation: 6,
            })
      : {}
  ), [themeType, theme.accent]);

  const cardStyle = [localStyles.cardBase, { backgroundColor: theme.card, borderColor: theme.border }, glowStyle];

  return (
    <View style={cardStyle}> 
      <TouchableOpacity style={[localStyles.accordionHeader, expanded && { marginBottom: 16 }]} onPress={onToggle}>
        <Text style={[localStyles.detailsTitle, { color: theme.text, marginBottom: 0 }]}>Dev Tools</Text>
        <Ionicons name={expanded ? 'chevron-up' : 'chevron-down'} size={24} color={theme.text} />
      </TouchableOpacity>

      {expanded && (
        <>
          <View style={{ height: 1, backgroundColor: theme.border, width: '100%', marginBottom: 16 }} />
          <Text style={[localStyles.detailsTitle, { color: theme.success }]}>Development Environment</Text>
          <View style={localStyles.detailsRow}><Text style={[localStyles.detailsLabel,{color:theme.text}]}>Environment:</Text><Text style={[localStyles.detailsValue,{color:theme.success}]}>Development</Text></View>
          <View style={localStyles.detailsRow}><Text style={[localStyles.detailsLabel,{color:theme.text}]}>NODE_ENV:</Text><Text style={[localStyles.detailsValue,{color:theme.success}]}>{process.env.NODE_ENV}</Text></View>
          <View style={localStyles.detailsRow}><Text style={[localStyles.detailsLabel,{color:theme.text}]}>Supabase URL:</Text><Text style={[localStyles.detailsValue,{color:theme.success}]}>{env.supabaseUrl}</Text></View>
          <View style={localStyles.detailsRow}><Text style={[localStyles.detailsLabel,{color:theme.text}]}>Supabase Key:</Text><Text style={[localStyles.detailsValue,{color:theme.success}]}>{env.supabaseAnonKey ? 'Present' : 'Missing'}</Text></View>
          <View style={localStyles.detailsRow}><Text style={[localStyles.detailsLabel,{color:theme.text}]}>API URL:</Text><Text style={[localStyles.detailsValue,{color:theme.success}]}>{env.apiUrl}</Text></View>
        </>
      )}
    </View>
  );
}

const localStyles = StyleSheet.create({
  cardBase: {
    padding: 16,
    borderRadius: 8,
    borderWidth: 1,
  },
  accordionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  detailsTitle: {
    fontSize: 17,
    fontWeight: '500',
    fontFamily: 'Inter_500Medium',
    marginBottom: 16,
  },
  detailsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  detailsLabel: {
    fontSize: 16,
    fontWeight: '600',
  },
  detailsValue: {
    fontSize: 16,
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
  },
}); 